"use strict";
'use client';

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.PopoverRoot = PopoverRoot;
var React = _interopRequireWildcard(require("react"));
var _useStableCallback = require("@base-ui-components/utils/useStableCallback");
var _useIsoLayoutEffect = require("@base-ui-components/utils/useIsoLayoutEffect");
var _useScrollLock = require("@base-ui-components/utils/useScrollLock");
var _floatingUiReact = require("../../floating-ui-react");
var _useTransitionStatus = require("../../utils/useTransitionStatus");
var _useOpenChangeComplete = require("../../utils/useOpenChangeComplete");
var _PopoverRootContext = require("./PopoverRootContext");
var _PopoverStore = require("../store/PopoverStore");
var _createBaseUIEventDetails = require("../../utils/createBaseUIEventDetails");
var _reasons = require("../../utils/reasons");
var _jsxRuntime = require("react/jsx-runtime");
function PopoverRootComponent({
  props
}) {
  const {
    children,
    open: openProp,
    defaultOpen: defaultOpenProp = false,
    onOpenChange,
    onOpenChangeComplete,
    modal = false,
    handle,
    triggerId: triggerIdProp,
    defaultTriggerId: defaultTriggerIdProp = null
  } = props;
  const store = _PopoverStore.PopoverStore.useStore(handle?.store, {
    open: openProp ?? defaultOpenProp,
    modal,
    activeTriggerId: triggerIdProp !== undefined ? triggerIdProp : defaultTriggerIdProp
  });
  store.useControlledProp('open', openProp, defaultOpenProp);
  store.useControlledProp('activeTriggerId', triggerIdProp, defaultTriggerIdProp);
  const open = store.useState('open');
  const triggerElements = store.useState('triggers');
  const activeTriggerId = store.useState('activeTriggerId');
  const positionerElement = store.useState('positionerElement');
  const activeTriggerElement = store.useState('activeTriggerElement');
  const payload = store.useState('payload');
  const openReason = store.useState('openReason');
  const openMethod = store.useState('openMethod');
  const {
    mounted,
    setMounted,
    transitionStatus
  } = (0, _useTransitionStatus.useTransitionStatus)(open);
  let resolvedTriggerId = null;
  if (mounted === true && triggerIdProp === undefined && triggerElements.size === 1) {
    resolvedTriggerId = triggerElements.keys().next().value || null;
  } else {
    resolvedTriggerId = activeTriggerId ?? null;
  }
  store.useContextCallback('onOpenChange', onOpenChange);
  store.useContextCallback('onOpenChangeComplete', onOpenChangeComplete);
  (0, _useIsoLayoutEffect.useIsoLayoutEffect)(() => {
    store.set('mounted', mounted);
    if (!mounted) {
      store.set('activeTriggerId', null);
    }
  }, [store, mounted]);
  (0, _useIsoLayoutEffect.useIsoLayoutEffect)(() => {
    if (open) {
      store.set('activeTriggerId', resolvedTriggerId);
      if (resolvedTriggerId == null) {
        store.set('payload', undefined);
      }
    }
  }, [store, resolvedTriggerId, open]);
  (0, _useScrollLock.useScrollLock)(open && modal === true && openReason !== _reasons.REASONS.triggerHover && openMethod !== 'touch', positionerElement);
  React.useEffect(() => {
    if (!open) {
      store.context.stickIfOpenTimeout.clear();
    }
  }, [store, open]);
  const createPopoverEventDetails = React.useCallback(reason => {
    const details = (0, _createBaseUIEventDetails.createChangeEventDetails)(reason);
    details.preventUnmountOnClose = () => {
      store.context.preventUnmountingRef.current = true;
    };
    return details;
  }, [store]);
  const handleUnmount = (0, _useStableCallback.useStableCallback)(() => {
    setMounted(false);
    store.update({
      stickIfOpen: true,
      openReason: null,
      activeTriggerId: null,
      mounted: false
    });
    onOpenChangeComplete?.(false);
  });
  const handleImperativeClose = React.useCallback(() => {
    store.setOpen(false, createPopoverEventDetails(_reasons.REASONS.imperativeAction));
  }, [store, createPopoverEventDetails]);
  (0, _useOpenChangeComplete.useOpenChangeComplete)({
    enabled: !store.context.preventUnmountingRef.current,
    open,
    ref: store.context.popupRef,
    onComplete() {
      if (!open) {
        handleUnmount();
      }
    }
  });
  React.useImperativeHandle(props.actionsRef, () => ({
    unmount: handleUnmount,
    close: handleImperativeClose
  }), [handleUnmount, handleImperativeClose]);
  const floatingContext = (0, _floatingUiReact.useFloatingRootContext)({
    elements: {
      reference: activeTriggerElement,
      floating: positionerElement,
      triggers: Array.from(triggerElements.values())
    },
    open,
    onOpenChange: store.setOpen
  });
  const dismiss = (0, _floatingUiReact.useDismiss)(floatingContext, {
    outsidePressEvent: {
      // Ensure `aria-hidden` on outside elements is removed immediately
      // on outside press when trapping focus.
      mouse: modal === 'trap-focus' ? 'sloppy' : 'intentional',
      touch: 'sloppy'
    }
  });
  const role = (0, _floatingUiReact.useRole)(floatingContext);
  const {
    getReferenceProps,
    getFloatingProps,
    getTriggerProps
  } = (0, _floatingUiReact.useInteractions)([dismiss, role]);
  store.useSyncedValues({
    transitionStatus,
    modal,
    activeTriggerProps: getReferenceProps(),
    inactiveTriggerProps: getTriggerProps(),
    popupProps: getFloatingProps(),
    floatingRootContext: floatingContext,
    nested: (0, _floatingUiReact.useFloatingParentNodeId)() != null
  });
  const popoverContext = React.useMemo(() => ({
    store
  }), [store]);
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_PopoverRootContext.PopoverRootContext.Provider, {
    value: popoverContext,
    children: typeof children === 'function' ? children({
      payload
    }) : children
  });
}

/**
 * Groups all parts of the popover.
 * Doesn’t render its own HTML element.
 *
 * Documentation: [Base UI Popover](https://base-ui.com/react/components/popover)
 */
function PopoverRoot(props) {
  if ((0, _PopoverRootContext.usePopoverRootContext)(true)) {
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(PopoverRootComponent, {
      props: props
    });
  }
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_floatingUiReact.FloatingTree, {
    children: /*#__PURE__*/(0, _jsxRuntime.jsx)(PopoverRootComponent, {
      props: props
    })
  });
}